"use client";

import { AlertTriangle, ExternalLink, Info } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  buildProjectDataOverviewIssueIndex,
  type ProjectDataOverviewIssue,
  type ProjectDataOverviewIssueIndex,
  type ProjectDataOverviewIssueSeverity,
} from "@/lib/aicheck/project-data-issue-index";
import {
  type ProjectDataReferenceTarget,
  parseProjectDataReference,
} from "@/lib/aicheck/project-data-reference";
import type {
  AggregatedProjectData,
  AICheckRunResult,
  FreeformCheckResult,
} from "@/types/aicheck";
import { DialogShell } from "./shared/DialogShell";

type RecordLike = Record<string, unknown>;

interface ProjectDataOverviewProps {
  projectData: AggregatedProjectData | null | undefined;
  checkResult?: AICheckRunResult | null;
  freeformResult?: FreeformCheckResult | null;
  conversionReport?: Record<string, unknown> | null;
  focusSource?: string | null;
  focusKey?: number;
  sourceLabel?: string | null;
  onCheckReferenceClick?: (issue: ProjectDataOverviewIssue) => void;
}

interface PersonRow {
  title: string;
  subtitle?: string | null;
  badges?: string[];
  details: Array<[string, string | null]>;
  notes?: string[];
}

interface ScheduleItem {
  key: string;
  item: RecordLike;
  arrangement?: RecordLike;
  reservationMatch?: ReservationMatchInfo;
  dayIndex: number;
  itemId: string | null;
  itemIndex: number;
  type: string | null;
  title: string;
  timeRange: string;
  isTimed: boolean;
  sortMinutes: number;
  detailLines: string[];
  priceParts: string[];
  status: string | null;
  issues: ProjectDataOverviewIssue[];
}

interface ItemTypeStyle {
  code: string;
  label: string;
  badgeClass: string;
  railClass: string;
  softClass: string;
}

type DetailModalGroupKey = "itinerary" | "arrangement" | "source";

interface DetailModalSection {
  group: DetailModalGroupKey;
  label: string;
  values: string[];
  muted?: boolean;
}

interface DetailModalGroup {
  key: DetailModalGroupKey;
  title: string;
  sections: DetailModalSection[];
}

interface DetailModalState {
  title: string;
  subtitle: string;
  sections: DetailModalSection[];
}

interface IssueDetailDialogState {
  title: string;
  subtitle: string;
  issues: ProjectDataOverviewIssue[];
}

interface ReservationMatchInfo {
  matched: boolean;
  reservationRow: string | null;
  method: string | null;
  confidence: string | null;
  reason: string | null;
  mainRange: string | null;
}

const DEFAULT_ITEM_TYPE_STYLE: ItemTypeStyle = {
  code: "ITM",
  label: "Item",
  badgeClass: "border-gray-200 bg-gray-50 text-gray-700",
  railClass: "bg-gray-400",
  softClass: "border-gray-200 bg-gray-50",
};

const ITEM_TYPE_STYLES: Record<string, ItemTypeStyle> = {
  ItineraryActivity: {
    code: "ACT",
    label: "Activity",
    badgeClass: "border-lime-200 bg-lime-50 text-lime-800",
    railClass: "bg-lime-500",
    softClass: "border-lime-200 bg-lime-50",
  },
  ItineraryExperience: {
    code: "EXP",
    label: "Experience",
    badgeClass: "border-violet-200 bg-violet-50 text-violet-700",
    railClass: "bg-violet-500",
    softClass: "border-violet-200 bg-violet-50",
  },
  ItineraryGuide: {
    code: "GDE",
    label: "Guide",
    badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-700",
    railClass: "bg-emerald-500",
    softClass: "border-emerald-200 bg-emerald-50",
  },
  ItineraryHireCar: {
    code: "CAR",
    label: "Hire car",
    badgeClass: "border-amber-200 bg-amber-50 text-amber-800",
    railClass: "bg-amber-500",
    softClass: "border-amber-200 bg-amber-50",
  },
  ItineraryHotel: {
    code: "HTL",
    label: "Hotel",
    badgeClass: "border-blue-200 bg-blue-50 text-blue-700",
    railClass: "bg-blue-500",
    softClass: "border-blue-200 bg-blue-50",
  },
  ItineraryOther: {
    code: "OTH",
    label: "Other",
    badgeClass: "border-slate-200 bg-slate-50 text-slate-700",
    railClass: "bg-slate-500",
    softClass: "border-slate-200 bg-slate-50",
  },
  ItineraryRestaurant: {
    code: "RST",
    label: "Restaurant",
    badgeClass: "border-rose-200 bg-rose-50 text-rose-700",
    railClass: "bg-rose-500",
    softClass: "border-rose-200 bg-rose-50",
  },
  ItineraryTransportation: {
    code: "TRN",
    label: "Transportation",
    badgeClass: "border-cyan-200 bg-cyan-50 text-cyan-700",
    railClass: "bg-cyan-500",
    softClass: "border-cyan-200 bg-cyan-50",
  },
};

const ARRANGEMENT_STATUS_TONES: Record<string, string> = {
  agent_arranged: "border-blue-200 bg-blue-50 text-blue-700",
  arrangement_not_started: "border-amber-200 bg-amber-50 text-amber-700",
  availability_confirmed: "border-indigo-200 bg-indigo-50 text-indigo-700",
  canceled: "border-gray-200 bg-gray-100 text-gray-600",
  change_request_to_arrangement:
    "border-orange-200 bg-orange-50 text-orange-700",
  final_booking_confirmed: "border-green-200 bg-green-50 text-green-700",
  invoice_confirmed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  tentative_booking_confirmed: "border-cyan-200 bg-cyan-50 text-cyan-700",
  waiting_sales_action: "border-purple-200 bg-purple-50 text-purple-700",
};

export function ProjectDataOverview({
  projectData,
  checkResult,
  freeformResult,
  conversionReport,
  focusKey,
  focusSource,
  onCheckReferenceClick,
  sourceLabel,
}: ProjectDataOverviewProps) {
  const [detailModal, setDetailModal] = useState<DetailModalState | null>(null);
  const [issueDetailDialog, setIssueDetailDialog] =
    useState<IssueDetailDialogState | null>(null);
  const [highlightedReferenceKey, setHighlightedReferenceKey] = useState<
    string | null
  >(null);
  const [overviewDocument, setOverviewDocument] = useState<Document | null>(
    null,
  );
  const overviewRootRef = useRef<HTMLDivElement>(null);
  const focusRequest = useMemo(
    () => ({
      key: focusKey,
      target: parseProjectDataReference(focusSource),
    }),
    [focusKey, focusSource],
  );
  const issueIndex = useMemo(
    () =>
      buildProjectDataOverviewIssueIndex(
        projectData,
        checkResult,
        freeformResult,
      ),
    [checkResult, freeformResult, projectData],
  );

  useEffect(() => {
    const ownerDocument = overviewRootRef.current?.ownerDocument;
    if (ownerDocument) setOverviewDocument(ownerDocument);
  }, []);

  useEffect(() => {
    const target = focusRequest.target;
    if (!target) return;

    const rafId = requestAnimationFrame(() => {
      const root = overviewRootRef.current;
      if (!root) return;
      const element = findProjectDataOverviewElement(target, root);
      if (!element) return;
      setHighlightedReferenceKey(element.dataset.projectDataRefKey ?? null);
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    });

    return () => cancelAnimationFrame(rafId);
  }, [focusRequest]);

  if (!projectData?.project) {
    return (
      <div className="rounded-md border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
        Project data is not available.
      </div>
    );
  }

  const project = asRecord(projectData.project);
  const itineraries = Array.isArray(projectData.itineraries)
    ? projectData.itineraries
    : [];
  const arrangements = Array.isArray(projectData.arrangements)
    ? projectData.arrangements
    : [];
  const arrangementByItemId = buildArrangementMap(arrangements);
  const reservationMatchByItemId = buildReservationMatchMap(conversionReport);
  const itemCount = itineraries.reduce(
    (count, itinerary) =>
      count + asRecords(asRecord(itinerary).itinerary_items).length,
    0,
  );
  const statusCounts = countArrangementStatuses(arrangements);
  const customers = buildCustomerRows(project);
  const agents = buildAgentRows(project);
  const assignees = buildAssigneeRows(project);
  const guideLanguages = buildGuideLanguageList(project);
  const sourceText =
    sourceLabel ?? stringValue(asRecord(projectData).sourceLabel) ?? null;
  const hasIssues = issueIndex.allIssues.length > 0;

  return (
    <div ref={overviewRootRef} className="space-y-4 text-gray-900">
      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase text-gray-400">
              Project
            </div>
            <h3 className="mt-1 truncate text-lg font-bold text-gray-900">
              {projectDisplayName(project)}
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              {sourceText && <Badge>{sourceText}</Badge>}
              {stringValue(project.id) && (
                <span>ID {stringValue(project.id)}</span>
              )}
              {stringValue(project.tour_code) && (
                <span>Tour code {stringValue(project.tour_code)}</span>
              )}
              {stringValue(project.status) && (
                <Badge tone="gray">
                  {labelize(stringValue(project.status))}
                </Badge>
              )}
            </div>
          </div>
          <div className="grid min-w-[280px] flex-1 grid-cols-2 gap-2 sm:max-w-xl sm:grid-cols-4">
            <Metric
              label="Trip dates"
              value={formatDateRange(
                stringValue(project.travel_start_date),
                stringValue(project.travel_end_date),
              )}
            />
            <Metric
              label="Travelers"
              value={
                stringValue(project.travelers_count)
                  ? `${stringValue(project.travelers_count)} PAX`
                  : "N/A"
              }
            />
            <Metric label="Days" value={itineraries.length.toLocaleString()} />
            <Metric label="Items" value={itemCount.toLocaleString()} />
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <InfoTile label="Arrangements" value={arrangements.length} />
          <InfoTile label="Hotels" value={numberOrText(project.hotel_count)} />
          <InfoTile
            label="Experiences"
            value={numberOrText(project.experience_count)}
          />
          <InfoTile
            label="Visit Japan"
            value={visitJapanTimesLabel(project.visit_japan_times)}
          />
        </div>

        {statusCounts.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {statusCounts.map(([status, count]) => (
              <StatusBadge key={status} status={status}>
                {labelize(status)} {count}
              </StatusBadge>
            ))}
          </div>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.85fr)]">
        <section className="rounded-lg border border-gray-200 bg-white">
          <SectionHeader title="Customers" count={customers.length} />
          <PersonRows rows={customers} emptyText="No customer records." />
        </section>

        <section className="rounded-lg border border-gray-200 bg-white">
          <SectionHeader title="Agent and Internal Contacts" />
          <div className="divide-y divide-gray-100">
            <KeyValueGroup
              rows={[
                ["Agency", nameFromRecord(asRecord(project.agency))],
                ["Agency tier", stringValue(asRecord(project.agency).tier)],
                ["Guide languages", guideLanguages.join(", ") || null],
              ]}
            />
            <PersonRows rows={agents} emptyText="No agent records." compact />
            <PersonRows rows={assignees} emptyText="No assignees." compact />
          </div>
        </section>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white">
        <SectionHeader title="Project Notes" />
        <div className="grid gap-3 px-4 py-3 lg:grid-cols-2">
          <NoteBlock
            label="Request"
            value={stringValue(project.request_note)}
          />
          <NoteBlock label="Budget" value={stringValue(project.budget_note)} />
          <NoteBlock
            label="Baggage"
            value={stringValue(project.baggage_note)}
          />
          <NoteBlock label="Internal note" value={stringValue(project.note)} />
          <NoteBlock
            label="Safari Portal"
            value={stringValue(project.safari_portal_url)}
          />
          <NoteBlock
            label="Google Drive"
            value={stringValue(project.google_drive_url)}
          />
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        <SectionHeader
          title="Itinerary"
          count={itineraries.length}
          subtitle={
            hasIssues
              ? `${itemCount.toLocaleString()} items · ${issueIndex.allIssues.length.toLocaleString()} linked issues`
              : `${itemCount.toLocaleString()} items`
          }
        />
        {issueIndex.projectIssues.length > 0 && (
          <ProjectIssueStrip
            issues={issueIndex.projectIssues}
            onShowIssueDetails={(issues) =>
              setIssueDetailDialog({
                title: "Project-level issues",
                subtitle: `${issues.length.toLocaleString()} checks without a specific itinerary day reference`,
                issues,
              })
            }
          />
        )}
        {itineraries.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            No itinerary days were found.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {itineraries.map((itinerary, index) => (
              <ItineraryDayBlock
                key={dayKey(itinerary, index)}
                itinerary={asRecord(itinerary)}
                index={index}
                arrangementByItemId={arrangementByItemId}
                reservationMatchByItemId={reservationMatchByItemId}
                issueIndex={issueIndex}
                highlightedReferenceKey={highlightedReferenceKey}
                onShowDetails={(item) =>
                  setDetailModal(buildItemDetailModal(item))
                }
                onShowIssueDetails={(dialog) => setIssueDetailDialog(dialog)}
              />
            ))}
          </div>
        )}
      </section>
      <ItemDetailDialog
        detail={detailModal}
        ownerDocument={overviewDocument}
        onClose={() => setDetailModal(null)}
      />
      <IssueDetailDialog
        detail={issueDetailDialog}
        ownerDocument={overviewDocument}
        onClose={() => setIssueDetailDialog(null)}
        onOpenCheckReference={onCheckReferenceClick}
      />
    </div>
  );
}

function ItineraryDayBlock({
  itinerary,
  index,
  arrangementByItemId,
  reservationMatchByItemId,
  issueIndex,
  highlightedReferenceKey,
  onShowDetails,
  onShowIssueDetails,
}: {
  itinerary: RecordLike;
  index: number;
  arrangementByItemId: Map<string, RecordLike>;
  reservationMatchByItemId: Map<string, ReservationMatchInfo>;
  issueIndex: ProjectDataOverviewIssueIndex;
  highlightedReferenceKey: string | null;
  onShowDetails: (item: ScheduleItem) => void;
  onShowIssueDetails: (dialog: IssueDetailDialogState) => void;
}) {
  const day = asRecord(itinerary.itinerary_day);
  const items = asRecords(itinerary.itinerary_items);
  const dayNumber = stringValue(day.day_number) ?? String(index + 1);
  const date = stringValue(day.date);
  const outline = stringValue(day.outline);
  const dayReferenceKey = projectDataDayReferenceKey(index);
  const scheduleItems = items
    .map((item, itemIndex) => {
      const itemId = stringValue(item.id);
      const embeddedArrangement = asRecord(item.itinerary_arrangement);
      const mappedArrangement = itemId
        ? arrangementByItemId.get(itemId)
        : undefined;
      const arrangement =
        embeddedArrangement && Object.keys(embeddedArrangement).length > 0
          ? embeddedArrangement
          : mappedArrangement;
      const reservationMatch = itemId
        ? reservationMatchByItemId.get(itemId)
        : undefined;
      const issueKey = itemId ?? `${index}:${itemIndex}`;
      const itemIssues = issueIndex.issuesByItemKey.get(issueKey) ?? [];
      return buildScheduleItem(
        item,
        arrangement,
        reservationMatch,
        itemId ?? `${dayNumber}:${itemIndex}`,
        index,
        itemIndex,
        itemIssues,
      );
    })
    .sort(
      (a, b) => a.sortMinutes - b.sortMinutes || a.title.localeCompare(b.title),
    );
  const allDayItems = scheduleItems.filter((item) => !item.isTimed);
  const timedItems = scheduleItems.filter((item) => item.isTimed);
  const typeCounts = countScheduleTypes(scheduleItems);
  const dayIssues = issueIndex.issuesByDayIndex.get(index) ?? [];
  const hasDayIssues = dayIssues.length > 0;
  const dayLayoutClass = hasDayIssues
    ? "lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(0,1fr)_minmax(220px,280px)]"
    : "lg:grid-cols-[220px_minmax(0,1fr)]";

  return (
    <div
      id={projectDataDayElementId(index)}
      data-project-data-day-id={stringValue(day.id) ?? undefined}
      data-project-data-day-index={index}
      data-project-data-day-number={dayNumber}
      data-project-data-ref-key={dayReferenceKey}
      className={`scroll-mt-24 grid gap-3 px-3 py-3 transition-colors ${dayLayoutClass} ${
        highlightedReferenceKey === dayReferenceKey
          ? "bg-blue-50/70 ring-2 ring-inset ring-blue-300"
          : ""
      }`}
    >
      <div className="min-w-0 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-bold text-gray-900">Day {dayNumber}</h4>
          {date && <Badge tone="gray">{date}</Badge>}
          <span className="text-xs text-gray-500">
            {items.length.toLocaleString()} items
          </span>
        </div>
        {typeCounts.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {typeCounts.map(([type, count]) => (
              <TypeBadge
                key={type ?? "unknown"}
                type={type}
                count={count}
                compact
              />
            ))}
          </div>
        )}
        {hasDayIssues && (
          <div className="mt-2">
            <IssueCountBadges issues={dayIssues} />
          </div>
        )}
        {outline && (
          <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-gray-600">
            {outline}
          </p>
        )}
      </div>

      <div className="min-w-0 space-y-2">
        {allDayItems.length > 0 && (
          <ScheduleLane title="All day / no time" count={allDayItems.length}>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {allDayItems.map((scheduleItem) => (
                <ScheduleItemCard
                  key={scheduleItem.key}
                  item={scheduleItem}
                  highlightedReferenceKey={highlightedReferenceKey}
                  onShowDetails={onShowDetails}
                  onShowIssueDetails={onShowIssueDetails}
                />
              ))}
            </div>
          </ScheduleLane>
        )}

        {timedItems.length > 0 && (
          <ScheduleLane title="Timed schedule" count={timedItems.length}>
            <div className="overflow-x-auto">
              <div className="min-w-[760px] divide-y divide-gray-100">
                {timedItems.map((scheduleItem) => (
                  <TimedScheduleRow
                    key={scheduleItem.key}
                    item={scheduleItem}
                    highlightedReferenceKey={highlightedReferenceKey}
                    onShowDetails={onShowDetails}
                    onShowIssueDetails={onShowIssueDetails}
                  />
                ))}
              </div>
            </div>
          </ScheduleLane>
        )}

        {scheduleItems.length === 0 && (
          <div className="rounded-md border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
            No itinerary items.
          </div>
        )}
      </div>
      {hasDayIssues && (
        <DayIssuePanel
          dayNumber={dayNumber}
          issues={dayIssues}
          onShowIssueDetails={onShowIssueDetails}
        />
      )}
    </div>
  );
}

function ScheduleLane({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <div className="text-[11px] font-bold uppercase text-gray-500">
          {title}
        </div>
        <Badge tone="gray">{count}</Badge>
      </div>
      <div className="p-2">{children}</div>
    </div>
  );
}

function TimedScheduleRow({
  item,
  highlightedReferenceKey,
  onShowDetails,
  onShowIssueDetails,
}: {
  item: ScheduleItem;
  highlightedReferenceKey: string | null;
  onShowDetails: (item: ScheduleItem) => void;
  onShowIssueDetails: (dialog: IssueDetailDialogState) => void;
}) {
  const style = itemTypeStyle(item.type);
  const referenceKey = projectDataItemReferenceKey(item);
  return (
    <div
      id={projectDataItemElementId(item.dayIndex, item.itemIndex)}
      data-project-data-day-index={item.dayIndex}
      data-project-data-item-id={item.itemId ?? undefined}
      data-project-data-item-index={item.itemIndex}
      data-project-data-item-type={item.type ?? undefined}
      data-project-data-ref-key={referenceKey}
      className={`scroll-mt-24 grid grid-cols-[72px_96px_minmax(0,1fr)_minmax(240px,0.85fr)] items-start gap-2 px-2 py-1.5 text-[11px] leading-tight transition-colors ${
        highlightedReferenceKey === referenceKey
          ? "rounded-md bg-blue-50 ring-2 ring-inset ring-blue-300"
          : ""
      }`}
    >
      <div className="whitespace-nowrap pt-0.5 font-semibold tabular-nums text-gray-700">
        {item.timeRange}
      </div>
      <div className="pt-0.5">
        <TypeBadge type={item.type} />
      </div>
      <div className="min-w-0">
        <div className="flex min-w-0 items-start gap-1.5">
          <span
            className={`mt-0.5 h-7 w-1 shrink-0 rounded-full ${style.railClass}`}
          />
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => onShowDetails(item)}
              className="block max-w-full truncate text-left font-semibold text-gray-900 transition-colors hover:text-blue-700 hover:underline"
              title={item.title}
            >
              {item.title}
            </button>
            <CompactDetailLines lines={item.detailLines} />
          </div>
        </div>
      </div>
      <TimedOpsColumn
        item={item}
        onShowDetails={onShowDetails}
        onShowIssueDetails={onShowIssueDetails}
      />
    </div>
  );
}

function ScheduleItemCard({
  item,
  highlightedReferenceKey,
  onShowDetails,
  onShowIssueDetails,
}: {
  item: ScheduleItem;
  highlightedReferenceKey: string | null;
  onShowDetails: (item: ScheduleItem) => void;
  onShowIssueDetails: (dialog: IssueDetailDialogState) => void;
}) {
  const style = itemTypeStyle(item.type);
  const referenceKey = projectDataItemReferenceKey(item);
  return (
    <div
      id={projectDataItemElementId(item.dayIndex, item.itemIndex)}
      data-project-data-day-index={item.dayIndex}
      data-project-data-item-id={item.itemId ?? undefined}
      data-project-data-item-index={item.itemIndex}
      data-project-data-item-type={item.type ?? undefined}
      data-project-data-ref-key={referenceKey}
      className={`scroll-mt-24 rounded-md border px-2.5 py-2 text-xs transition-colors ${style.softClass} ${
        highlightedReferenceKey === referenceKey ? "ring-2 ring-blue-300" : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <TypeBadge type={item.type} />
        <span className="shrink-0 font-semibold tabular-nums text-gray-500">
          {item.timeRange}
        </span>
      </div>
      <button
        type="button"
        onClick={() => onShowDetails(item)}
        className="mt-1 block max-w-full truncate text-left font-semibold text-gray-900 transition-colors hover:text-blue-700 hover:underline"
        title={item.title}
      >
        {item.title}
      </button>
      <CompactDetailLines lines={item.detailLines} />
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <ArrangementStatus item={item} />
        <ArrangementLinkBadge item={item} />
        <ItemIssueButton item={item} onShowIssueDetails={onShowIssueDetails} />
        {item.priceParts.slice(0, 2).map((part) => (
          <Badge key={part} tone="gray">
            {part}
          </Badge>
        ))}
        <DetailsButton item={item} onShowDetails={onShowDetails} />
      </div>
    </div>
  );
}

function ProjectIssueStrip({
  issues,
  onShowIssueDetails,
}: {
  issues: ProjectDataOverviewIssue[];
  onShowIssueDetails: (issues: ProjectDataOverviewIssue[]) => void;
}) {
  const visibleIssues = issues.slice(0, 3);
  const remaining = issues.length - visibleIssues.length;
  return (
    <div className="border-b border-gray-200 bg-amber-50/40 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-xs font-bold uppercase text-amber-700">
              Project-level checks
            </div>
            <IssueCountBadges issues={issues} />
          </div>
          <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-amber-900">
            {visibleIssues.map((issue) => (
              <span
                key={issue.id}
                className="max-w-full truncate rounded border border-amber-200 bg-white/70 px-2 py-0.5"
              >
                {issue.title}
              </span>
            ))}
            {remaining > 0 && (
              <span className="rounded border border-amber-200 bg-white/70 px-2 py-0.5 font-semibold">
                +{remaining} more
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onShowIssueDetails(issues)}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-amber-200 bg-white px-2 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100"
          title="Show project-level check details"
        >
          <Info size={13} aria-hidden="true" />
          Details
        </button>
      </div>
    </div>
  );
}

function DayIssuePanel({
  dayNumber,
  issues,
  onShowIssueDetails,
}: {
  dayNumber: string;
  issues: ProjectDataOverviewIssue[];
  onShowIssueDetails: (dialog: IssueDetailDialogState) => void;
}) {
  const visibleIssues = issues.slice(0, 3);
  const remaining = issues.length - visibleIssues.length;
  return (
    <aside className="hidden min-w-0 rounded-md border border-amber-200 bg-amber-50/50 px-2.5 py-2 xl:block">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <AlertTriangle size={13} className="shrink-0 text-amber-600" />
          <div className="truncate text-[11px] font-bold uppercase text-amber-700">
            Checks
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            onShowIssueDetails({
              title: `Day ${dayNumber} check issues`,
              subtitle: `${issues.length.toLocaleString()} linked issue${
                issues.length === 1 ? "" : "s"
              }`,
              issues,
            })
          }
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-amber-200 bg-white text-amber-700 transition-colors hover:bg-amber-100"
          title={`Show all linked check issues for Day ${dayNumber}`}
          aria-label={`Show all linked check issues for Day ${dayNumber}`}
        >
          <Info size={13} aria-hidden="true" />
        </button>
      </div>
      <IssueCountBadges issues={issues} className="mt-2" />
      <div className="mt-2 space-y-1.5">
        {visibleIssues.map((issue) => (
          <button
            key={issue.id}
            type="button"
            onClick={() =>
              onShowIssueDetails({
                title: issue.title,
                subtitle: `${issue.checkName} · ${checkCategoryLabel(
                  issue.category,
                )}`,
                issues: [issue],
              })
            }
            className="block w-full rounded border border-amber-100 bg-white/80 px-2 py-1.5 text-left transition-colors hover:border-amber-200 hover:bg-white"
          >
            <div className="flex items-center gap-1.5">
              <IssueSeverityDot severity={issue.severity} />
              <span className="min-w-0 truncate text-[11px] font-semibold text-gray-800">
                {issue.title}
              </span>
            </div>
            <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-gray-600">
              {issue.summary}
            </div>
          </button>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            onClick={() =>
              onShowIssueDetails({
                title: `Day ${dayNumber} check issues`,
                subtitle: `${issues.length.toLocaleString()} linked issues`,
                issues,
              })
            }
            className="text-[11px] font-semibold text-amber-700 hover:text-amber-800"
          >
            +{remaining} more
          </button>
        )}
      </div>
    </aside>
  );
}

function ItemIssueButton({
  item,
  onShowIssueDetails,
}: {
  item: ScheduleItem;
  onShowIssueDetails: (dialog: IssueDetailDialogState) => void;
}) {
  if (item.issues.length === 0) return null;
  const count = item.issues.length;
  return (
    <button
      type="button"
      onClick={() =>
        onShowIssueDetails({
          title: `${item.title} check issues`,
          subtitle: `${count.toLocaleString()} linked issue${
            count === 1 ? "" : "s"
          }`,
          issues: item.issues,
        })
      }
      className="inline-flex max-w-full items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-100"
      title="Show linked check issues for this itinerary item"
      aria-label="Show linked check issues for this itinerary item"
    >
      <AlertTriangle size={12} aria-hidden="true" />
      <span>Issues {count}</span>
    </button>
  );
}

function IssueCountBadges({
  issues,
  className = "",
}: {
  issues: ProjectDataOverviewIssue[];
  className?: string;
}) {
  const counts = countIssuesBySeverity(issues);
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {(["critical", "warning", "info"] as const).map((severity) =>
        counts[severity] > 0 ? (
          <IssueSeverityBadge
            key={severity}
            severity={severity}
            count={counts[severity]}
          />
        ) : null,
      )}
    </div>
  );
}

function IssueSeverityBadge({
  severity,
  count,
}: {
  severity: ProjectDataOverviewIssueSeverity;
  count: number;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-bold ${issueSeverityClassName(
        severity,
      )}`}
    >
      <IssueSeverityDot severity={severity} />
      {labelize(severity)} {count}
    </span>
  );
}

function IssueSeverityDot({
  severity,
}: {
  severity: ProjectDataOverviewIssueSeverity;
}) {
  const className =
    severity === "critical"
      ? "bg-red-500"
      : severity === "warning"
        ? "bg-amber-500"
        : "bg-sky-500";
  return (
    <span
      className={`h-1.5 w-1.5 shrink-0 rounded-full ${className}`}
      aria-hidden="true"
    />
  );
}

function TypeBadge({
  type,
  count,
  compact = false,
}: {
  type: string | null;
  count?: number;
  compact?: boolean;
}) {
  const style = itemTypeStyle(type);
  const showLabel = !compact && count == null;
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-bold ${style.badgeClass}`}
      title={style.label}
    >
      <span className="tabular-nums">{style.code}</span>
      {showLabel && <span className="truncate">{style.label}</span>}
      {typeof count === "number" && (
        <span className="font-semibold opacity-80">{count}</span>
      )}
    </span>
  );
}

function CompactDetailLines({ lines }: { lines: string[] }) {
  if (lines.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-gray-600">
      {lines.slice(0, 4).map((line) => (
        <span key={line} className="max-w-full truncate">
          {line}
        </span>
      ))}
    </div>
  );
}

function TimedOpsColumn({
  item,
  onShowDetails,
  onShowIssueDetails,
}: {
  item: ScheduleItem;
  onShowDetails: (item: ScheduleItem) => void;
  onShowIssueDetails: (dialog: IssueDetailDialogState) => void;
}) {
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1">
        <ArrangementStatus item={item} />
        <ArrangementLinkBadge item={item} />
        <ItemIssueButton item={item} onShowIssueDetails={onShowIssueDetails} />
        {item.priceParts.length > 0 ? (
          compactPriceParts(item.priceParts).map((part) => (
            <span
              key={`${part.label}:${part.value}`}
              className="inline-flex max-w-full items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-medium text-gray-600"
            >
              <span className="font-bold text-gray-500">{part.label}</span>
              <span className="tabular-nums">{part.value}</span>
            </span>
          ))
        ) : (
          <span className="text-gray-400">No price data</span>
        )}
        <DetailsButton item={item} onShowDetails={onShowDetails} />
      </div>
      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-gray-600">
        {stringValue(item.arrangement?.payment_deadline) && (
          <span className="max-w-full truncate">
            Payment: {stringValue(item.arrangement?.payment_deadline)}
          </span>
        )}
        {stringValue(item.arrangement?.note) && (
          <span className="max-w-full truncate">
            {stringValue(item.arrangement?.note)}
          </span>
        )}
        {stringValue(item.arrangement?.arrangement_note) && (
          <span className="max-w-full truncate">
            {stringValue(item.arrangement?.arrangement_note)}
          </span>
        )}
      </div>
    </div>
  );
}

function DetailsButton({
  item,
  onShowDetails,
}: {
  item: ScheduleItem;
  onShowDetails: (item: ScheduleItem) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onShowDetails(item)}
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800"
      title="Show full details in a popup"
      aria-label={`Show full details in a popup for ${item.title}`}
    >
      <Info size={13} aria-hidden="true" />
    </button>
  );
}

function ArrangementLinkBadge({ item }: { item: ScheduleItem }) {
  const arrangement = item.arrangement;
  if (!arrangement) return null;
  return (
    <span
      className="inline-flex max-w-full items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] font-semibold text-sky-700"
      title="This itinerary item is linked to a projectData arrangement record."
    >
      Arrangement
    </span>
  );
}

function ArrangementStatus({ item }: { item: ScheduleItem }) {
  if (item.status) {
    return (
      <StatusBadge status={item.status}>{labelize(item.status)}</StatusBadge>
    );
  }
  return item.arrangement ? (
    <Badge tone="gray">Status N/A</Badge>
  ) : (
    <Badge tone="gray">No arrangement</Badge>
  );
}

function ItemDetailDialog({
  detail,
  ownerDocument,
  onClose,
}: {
  detail: DetailModalState | null;
  ownerDocument: Document | null;
  onClose: () => void;
}) {
  if (!detail) return null;

  const groups = buildDetailModalGroups(detail.sections);
  return (
    <DialogShell
      onClose={onClose}
      ownerDocument={ownerDocument}
      portalToOwnerBody
      scrollLock="html-and-body"
      trapBackdropScroll
      ariaLabelledBy="project-data-item-detail-title"
      backdropClassName="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden bg-black/40 p-4"
      backdropOverlayClassName="absolute inset-0 cursor-default"
      backdropAriaLabel="Close itinerary item detail backdrop"
      panelClassName="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div className="min-w-0">
          <h3
            id="project-data-item-detail-title"
            className="truncate text-base font-semibold text-gray-900"
          >
            {detail.title}
          </h3>
          <div className="mt-1 text-xs text-gray-500">{detail.subtitle}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2 py-1 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          aria-label="Close itinerary item detail"
        >
          Close
        </button>
      </div>
      <div className="min-h-0 overflow-y-auto overscroll-contain bg-gray-50 p-4">
        <div className="space-y-4">
          {groups.map((group) => (
            <section
              key={group.key}
              className={detailGroupClassName(group.key)}
            >
              <div className="flex items-center justify-between gap-3 border-b border-gray-200/70 pb-2">
                <div className={detailGroupTitleClassName(group.key)}>
                  {group.title}
                </div>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {group.sections.map((section) => (
                  <section
                    key={section.label}
                    className={detailSectionCardClassName(section)}
                  >
                    <div className="text-[11px] font-bold uppercase text-gray-400">
                      {section.label}
                    </div>
                    <div className={detailSectionValuesClassName(section)}>
                      {section.values.map((value) => (
                        <div
                          key={value}
                          className="whitespace-pre-wrap break-words"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </DialogShell>
  );
}

function IssueDetailDialog({
  detail,
  ownerDocument,
  onClose,
  onOpenCheckReference,
}: {
  detail: IssueDetailDialogState | null;
  ownerDocument: Document | null;
  onClose: () => void;
  onOpenCheckReference?: (issue: ProjectDataOverviewIssue) => void;
}) {
  if (!detail) return null;

  return (
    <DialogShell
      onClose={onClose}
      ownerDocument={ownerDocument}
      portalToOwnerBody
      scrollLock="html-and-body"
      trapBackdropScroll
      ariaLabelledBy="project-data-issue-detail-title"
      backdropClassName="fixed inset-0 z-[1000] flex items-center justify-center overflow-hidden bg-black/40 p-4"
      backdropOverlayClassName="absolute inset-0 cursor-default"
      backdropAriaLabel="Close check issue detail backdrop"
      panelClassName="relative z-10 flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div className="min-w-0">
          <h3
            id="project-data-issue-detail-title"
            className="truncate text-base font-semibold text-gray-900"
          >
            {detail.title}
          </h3>
          <div className="mt-1 text-xs text-gray-500">{detail.subtitle}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md px-2 py-1 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          aria-label="Close check issue detail"
        >
          Close
        </button>
      </div>
      <div className="min-h-0 overflow-y-auto overscroll-contain bg-gray-50 p-4">
        <div className="space-y-3">
          {detail.issues.map((issue) => (
            <section
              key={issue.id}
              className="rounded-lg border border-gray-200 bg-white px-3 py-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <IssueSeverityBadge severity={issue.severity} count={1} />
                    <Badge tone="gray">{sourceKindLabel(issue)}</Badge>
                    <Badge tone="gray">
                      {checkCategoryLabel(issue.category)}
                    </Badge>
                  </div>
                  <h4 className="mt-2 text-sm font-bold text-gray-900">
                    {issue.title}
                  </h4>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {issue.checkName}
                  </div>
                </div>
                {onOpenCheckReference && (
                  <button
                    type="button"
                    onClick={() => {
                      onOpenCheckReference(issue);
                      onClose();
                    }}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                    title={
                      issue.sourceKind === "freeform"
                        ? "Open this Freeform Review result"
                        : "Open this check in the check results view"
                    }
                  >
                    <ExternalLink size={13} aria-hidden="true" />
                    {issue.sourceKind === "freeform"
                      ? "Open Freeform"
                      : "Open check"}
                  </button>
                )}
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <section className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                  <div className="text-[11px] font-bold uppercase text-gray-400">
                    Summary
                  </div>
                  <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">
                    {issue.summary}
                  </div>
                </section>
                <section className="rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
                  <div className="text-[11px] font-bold uppercase text-gray-400">
                    Reference
                  </div>
                  <div className="mt-2 space-y-1 text-sm leading-relaxed text-gray-700">
                    <div>{issueScopeLabel(issue)}</div>
                    {issue.reference.source && (
                      <div className="break-words font-mono text-xs text-gray-500">
                        {issue.reference.source}
                      </div>
                    )}
                  </div>
                </section>
                <section className="rounded-md border border-gray-200 bg-white px-3 py-3 md:col-span-2">
                  <div className="text-[11px] font-bold uppercase text-gray-400">
                    Detail
                  </div>
                  <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">
                    {issue.detail}
                  </div>
                </section>
              </div>
            </section>
          ))}
        </div>
      </div>
    </DialogShell>
  );
}

function buildDetailModalGroups(
  sections: DetailModalSection[],
): DetailModalGroup[] {
  const groups: Array<Pick<DetailModalGroup, "key" | "title">> = [
    { key: "itinerary", title: "Itinerary item" },
    { key: "arrangement", title: "Arrangement" },
    { key: "source", title: "Excel source match (supplemental)" },
  ];
  return groups
    .map(
      (group): DetailModalGroup => ({
        ...group,
        sections: sections.filter((section) => section.group === group.key),
      }),
    )
    .filter((group) => group.sections.length > 0);
}

function detailGroupClassName(group: DetailModalGroupKey): string {
  return group === "source"
    ? "rounded-lg border border-gray-100 bg-gray-50 px-3 py-3"
    : "rounded-lg border border-gray-200 bg-white px-3 py-3";
}

function detailGroupTitleClassName(group: DetailModalGroupKey): string {
  return group === "source"
    ? "text-xs font-bold uppercase text-gray-400"
    : "text-xs font-bold uppercase text-gray-700";
}

function detailSectionCardClassName(section: DetailModalSection): string {
  return section.muted
    ? "rounded-md border border-gray-100 bg-white/70 px-3 py-3 text-gray-500 md:col-span-2"
    : "rounded-md border border-gray-200 bg-white px-3 py-3";
}

function detailSectionValuesClassName(section: DetailModalSection): string {
  return section.muted
    ? "mt-2 space-y-1 text-sm leading-relaxed text-gray-600"
    : "mt-2 space-y-1 text-sm leading-relaxed text-gray-800";
}

function PersonRows({
  rows,
  emptyText,
  compact = false,
}: {
  rows: PersonRow[];
  emptyText: string;
  compact?: boolean;
}) {
  if (rows.length === 0) {
    return <div className="px-4 py-3 text-sm text-gray-500">{emptyText}</div>;
  }
  return (
    <div className="divide-y divide-gray-100">
      {rows.map((row, index) => (
        <div
          key={`${row.title}:${index}`}
          className={compact ? "px-4 py-2.5" : "px-4 py-3"}
        >
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 truncate text-sm font-semibold text-gray-900">
              {row.title}
            </div>
            {row.badges?.map((badge) => (
              <Badge key={badge} tone="gray">
                {badge}
              </Badge>
            ))}
          </div>
          {row.subtitle && (
            <div className="mt-0.5 text-xs text-gray-500">{row.subtitle}</div>
          )}
          <KeyValueInline rows={row.details} />
          {row.notes && row.notes.length > 0 && (
            <div className="mt-2 space-y-1 text-xs leading-relaxed text-gray-600">
              {row.notes.map((note) => (
                <div key={note} className="whitespace-pre-wrap">
                  {note}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function KeyValueGroup({
  rows,
}: {
  rows: Array<[string, string | null | undefined]>;
}) {
  const visibleRows = rows.filter(([, value]) => value);
  if (visibleRows.length === 0) return null;
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-2">
      {visibleRows.map(([label, value]) => (
        <div key={label}>
          <div className="text-[11px] font-semibold uppercase text-gray-400">
            {label}
          </div>
          <div className="mt-0.5 break-words text-sm text-gray-800">
            {value}
          </div>
        </div>
      ))}
    </div>
  );
}

function KeyValueInline({
  rows,
}: {
  rows: Array<[string, string | null | undefined]>;
}) {
  const visibleRows = rows.filter(([, value]) => value);
  if (visibleRows.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
      {visibleRows.map(([label, value]) => (
        <span key={label}>
          <span className="font-semibold text-gray-500">{label}:</span> {value}
        </span>
      ))}
    </div>
  );
}

function SectionHeader({
  title,
  count,
  subtitle,
}: {
  title: string;
  count?: number;
  subtitle?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        {subtitle && <span>{subtitle}</span>}
        {typeof count === "number" && (
          <Badge tone="gray">{count.toLocaleString()}</Badge>
        )}
      </div>
    </div>
  );
}

function NoteBlock({ label, value }: { label: string; value: string | null }) {
  if (!value) {
    return (
      <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
        <div className="text-[11px] font-semibold uppercase text-gray-400">
          {label}
        </div>
        <div className="mt-1 text-xs text-gray-400">N/A</div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
      <div className="text-[11px] font-semibold uppercase text-gray-400">
        {label}
      </div>
      <div className="mt-1 max-h-24 overflow-y-auto whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-700">
        {value}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase text-gray-400">
        {label}
      </div>
      <div className="mt-1 truncate text-sm font-bold text-gray-900">
        {value}
      </div>
    </div>
  );
}

function InfoTile({
  label,
  value,
}: {
  label: string;
  value: string | number | null;
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase text-gray-400">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-gray-800">
        {value ?? "N/A"}
      </div>
    </div>
  );
}

function Badge({
  children,
  tone = "blue",
}: {
  children: ReactNode;
  tone?: "blue" | "gray";
}) {
  const className =
    tone === "gray"
      ? "border-gray-200 bg-gray-50 text-gray-600"
      : "border-blue-200 bg-blue-50 text-blue-700";
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${className}`}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function StatusBadge({
  status,
  children,
}: {
  status: string;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${
        ARRANGEMENT_STATUS_TONES[status] ??
        "border-gray-200 bg-gray-50 text-gray-600"
      }`}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function countIssuesBySeverity(
  issues: ProjectDataOverviewIssue[],
): Record<ProjectDataOverviewIssueSeverity, number> {
  return issues.reduce(
    (counts, issue) => {
      counts[issue.severity] += 1;
      return counts;
    },
    { critical: 0, warning: 0, info: 0 },
  );
}

function issueSeverityClassName(
  severity: ProjectDataOverviewIssueSeverity,
): string {
  if (severity === "critical") {
    return "border-red-200 bg-red-50 text-red-700";
  }
  if (severity === "warning") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-sky-200 bg-sky-50 text-sky-700";
}

function sourceKindLabel(issue: ProjectDataOverviewIssue): string {
  if (issue.sourceKind === "freeform") return "Freeform Review";
  return issue.sourceKind === "rule" ? "Rule" : "AI check";
}

function checkCategoryLabel(category: ProjectDataOverviewIssue["category"]) {
  if (category === "need_review") return "Need Review";
  if (category === "reminders") return "Reminders";
  return "Basic Requests";
}

function issueScopeLabel(issue: ProjectDataOverviewIssue): string {
  const reference = issue.reference;
  if (reference.kind === "item") {
    const day =
      reference.dayNumber ??
      (reference.dayIndex != null ? reference.dayIndex + 1 : null);
    const item =
      reference.itemIndex != null ? ` item ${reference.itemIndex + 1}` : "";
    const type = reference.itemType ? ` (${labelize(reference.itemType)})` : "";
    return `Itinerary Day ${day ?? "?"}${item}${type}`;
  }
  if (reference.kind === "day") {
    const day =
      reference.dayNumber ??
      (reference.dayIndex != null ? reference.dayIndex + 1 : null);
    return `Itinerary Day ${day ?? "?"}`;
  }
  return "Project-level reference";
}

function buildArrangementMap(
  arrangements: AggregatedProjectData["arrangements"],
): Map<string, RecordLike> {
  const map = new Map<string, RecordLike>();
  for (const arrangement of arrangements) {
    const record = asRecord(arrangement);
    const id = stringValue(record.itinerary_item_id);
    if (id) map.set(id, record);
  }
  return map;
}

function buildReservationMatchMap(
  conversionReport: Record<string, unknown> | null | undefined,
): Map<string, ReservationMatchInfo> {
  const map = new Map<string, ReservationMatchInfo>();
  const sourceMap = asRecord(conversionReport?.sourceMap);
  for (const item of asRecords(sourceMap.itineraryItems)) {
    const id = stringValue(item.id);
    if (!id) continue;
    const reservationRow = stringValue(item.reservationRow);
    map.set(id, {
      matched: reservationRow != null,
      reservationRow,
      method: stringValue(item.reservationMatchMethod),
      confidence: stringValue(item.reservationMatchConfidence),
      reason: stringValue(item.reservationMatchReason),
      mainRange: stringValue(item.mainRange),
    });
  }
  return map;
}

function buildCustomerRows(project: RecordLike): PersonRow[] {
  return asRecords(project.customer_projects).map((customerProject) => {
    const customer = asRecord(customerProject.customer);
    const nationality = nameFromRecord(asRecord(customer.nationality));
    const residence = nameFromRecord(asRecord(customer.residence_country));
    const badges = [
      boolValue(customerProject.is_primary) ? "Primary" : null,
      stringValue(customer.honorific),
      stringValue(customer.gender),
    ].filter((value): value is string => value != null);

    return {
      title: nameFromRecord(customer) ?? "Unnamed customer",
      badges,
      details: [
        ["Phone", stringValue(customer.phone_number)],
        ["Age", stringValue(customer.age)],
        ["Nationality", nationality],
        ["Residence", residence],
        ["Japan visits", stringValue(customer.japan_visit_times)],
      ],
      notes: [
        labeledNote("Visit history", customer.japan_visit_history),
        labeledNote("Food request", customer.food_request),
        labeledNote("Allergy", customer.food_allergy),
        labeledNote("Special requirements", customer.special_requirements_note),
        labeledNote("Medical", customer.medical_requirements_note),
        labeledNote("Anniversary", customer.anniversary_note),
        labeledNote("Note", customer.note),
      ].filter((value): value is string => value != null),
    };
  });
}

function buildAgentRows(project: RecordLike): PersonRow[] {
  return asRecords(project.agent_projects).map((agentProject) => {
    const agent = asRecord(agentProject.agent);
    const agencies = asRecords(agent.agencies)
      .map((agency) => nameFromRecord(agency))
      .filter((value): value is string => value != null);
    return {
      title: nameFromRecord(agent) ?? "Unnamed agent",
      subtitle: stringValue(agentProject.assignment_type)
        ? labelize(stringValue(agentProject.assignment_type))
        : null,
      badges: agencies.slice(0, 2),
      details: [
        ["Email", stringValue(agent.email)],
        ["Phone", stringValue(agent.phone)],
        ["Country", nameFromRecord(asRecord(agent.country))],
        ["Experience", stringValue(agent.experience_years)],
      ],
      notes: [
        labeledNote("Character", agent.agents_character),
        labeledNote("Commission", agent.commission_notes),
        labeledNote("Notes", agent.notes),
      ].filter((value): value is string => value != null),
    };
  });
}

function buildAssigneeRows(project: RecordLike): PersonRow[] {
  return asRecords(project.project_assignees).map((assignee) => {
    const user = asRecord(assignee.user);
    return {
      title:
        firstText(
          user.display_name,
          user.name,
          user.email,
          user.username,
          assignee.id,
        ) ?? "Unnamed assignee",
      subtitle: stringValue(assignee.assignment_type)
        ? labelize(stringValue(assignee.assignment_type))
        : null,
      details: [
        ["Email", stringValue(user.email)],
        ["Role", stringValue(user.role)],
      ],
    };
  });
}

function buildGuideLanguageList(project: RecordLike): string[] {
  return asRecords(project.project_guide_languages)
    .map((link) => {
      const language = asRecord(link.language);
      const languageName = nameFromRecord(language);
      const languageType = stringValue(link.language_type);
      if (!languageName) return null;
      return languageType
        ? `${languageName} (${labelize(languageType)})`
        : languageName;
    })
    .filter((value): value is string => value != null);
}

function countArrangementStatuses(
  arrangements: AggregatedProjectData["arrangements"],
): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const arrangement of arrangements) {
    const status = stringValue(asRecord(arrangement).arrangement_status);
    if (!status) continue;
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
}

function buildScheduleItem(
  item: RecordLike,
  arrangement: RecordLike | undefined,
  reservationMatch: ReservationMatchInfo | undefined,
  key: string,
  dayIndex: number,
  itemIndex: number,
  issues: ProjectDataOverviewIssue[],
): ScheduleItem {
  const detail = asRecord(item.detail);
  const startTime = stringValue(item.start_time);
  const endTime = stringValue(item.end_time);
  const isTimed = startTime != null || endTime != null;
  const type = stringValue(item.itinerary_itemable_type);
  const status = stringValue(arrangement?.arrangement_status);
  return {
    key,
    item,
    arrangement,
    reservationMatch,
    dayIndex,
    itemId: stringValue(item.id),
    itemIndex,
    type,
    title: itemTitle(item),
    timeRange: formatTimeRange(startTime, endTime),
    isTimed,
    sortMinutes: isTimed
      ? (timeToMinutes(startTime ?? endTime) ?? 24 * 60)
      : -1,
    detailLines: itemDetailLines(item, detail, arrangement),
    priceParts: [
      moneyPart("Sell", item.selling_price),
      moneyPart("Quote", item.quoted_supplier_cost),
      moneyPart("Actual", arrangement?.actual_supplier_cost),
      quantityPart(item.quantity),
    ].filter((part): part is string => part != null),
    status,
    issues,
  };
}

function buildItemDetailModal(item: ScheduleItem): DetailModalState {
  const style = itemTypeStyle(item.type);
  const itemRecord = item.item;
  const arrangement = item.arrangement;
  const reservationMatch = item.reservationMatch;
  const sections = [
    detailSection("Itinerary summary", [
      `Type: ${style.code} ${style.label}`,
      `Time: ${item.timeRange}`,
    ]),
    detailSection("Itinerary record", [
      labeledNote("Itinerary item ID", itemRecord.id),
      labeledNote("Itemable type", itemRecord.itinerary_itemable_type),
      labeledNote("Itemable ID", itemRecord.itinerary_itemable_id),
      labeledNote("Start day", itemRecord.start_day_number),
      labeledNote("End day", itemRecord.end_day_number),
      labeledNote("Start time", itemRecord.start_time),
      labeledNote("End time", itemRecord.end_time),
    ]),
    detailSection("Itinerary details", item.detailLines),
    detailSection("Itinerary pricing", item.priceParts),
    detailSection("Itinerary notes", [
      labeledNote("Internal note", itemRecord.to_internal_note),
      labeledNote("Agent note", itemRecord.to_agent_note),
      labeledNote("Supplier note", itemRecord.to_supplier_note),
      labeledNote("Customer note", itemRecord.to_customer_note),
      labeledNote("Guide note", itemRecord.to_guide_note),
    ]),
    detailSection(
      "Arrangement summary",
      [
        arrangement
          ? "Arrangement record: linked to this itinerary item"
          : "Arrangement record: not linked",
        item.status ? `Arrangement status: ${labelize(item.status)}` : null,
      ],
      { group: "arrangement" },
    ),
    detailSection(
      "Arrangement record",
      [
        labeledNote("Arrangement ID", arrangement?.id),
        labeledNote("Linked itinerary item ID", arrangement?.itinerary_item_id),
        labeledNote("Arrangement status", arrangement?.arrangement_status),
      ],
      { group: "arrangement" },
    ),
    detailSection(
      "Arrangement payment and cost",
      [
        labeledNote("Expense status", arrangement?.expense_status),
        labeledNote("Payment schedule", arrangement?.payment_schedule),
        labeledNote("Payment method", arrangement?.payment_method),
        labeledNote("Quoted supplier cost", arrangement?.quoted_supplier_cost),
        labeledNote("Actual supplier cost", arrangement?.actual_supplier_cost),
      ],
      { group: "arrangement" },
    ),
    detailSection(
      "Arrangement notes",
      [
        labeledNote("Payment deadline", arrangement?.payment_deadline),
        labeledNote("Cancel policy", arrangement?.cancel_policy),
        labeledNote("Note", arrangement?.note),
        labeledNote("Arrangement note", arrangement?.arrangement_note),
      ],
      { group: "arrangement" },
    ),
    detailSection(
      "Excel source match (supplemental)",
      [
        reservationMatch
          ? reservationMatch.matched
            ? "This is conversion provenance from the Excel reservation sheet."
            : "No matching Excel reservation row was found during conversion."
          : null,
        labeledNote("Main tab range", reservationMatch?.mainRange),
        labeledNote("Reservation row", reservationMatch?.reservationRow),
        labeledNote("Match method", reservationMatch?.method),
        labeledNote("Match confidence", reservationMatch?.confidence),
        labeledNote("Match reason", reservationMatch?.reason),
      ],
      { group: "source", muted: true },
    ),
  ].filter((section): section is DetailModalSection => section != null);

  return {
    title: item.title,
    subtitle: `${item.timeRange} · ${style.label}`,
    sections:
      sections.length > 0
        ? sections
        : [
            {
              group: "itinerary",
              label: "Details",
              values: ["No additional details."],
            },
          ],
  };
}

function detailSection(
  label: string,
  values: Array<string | null>,
  options?: { group?: DetailModalGroupKey; muted?: boolean },
): DetailModalSection | null {
  const visibleValues = values.filter(
    (value): value is string => value != null,
  );
  return visibleValues.length > 0
    ? {
        group: options?.group ?? "itinerary",
        label,
        values: visibleValues,
        muted: options?.muted,
      }
    : null;
}

function countScheduleTypes(
  items: ScheduleItem[],
): Array<[string | null, number]> {
  const counts = new Map<string | null, number>();
  for (const item of items) {
    counts.set(item.type, (counts.get(item.type) ?? 0) + 1);
  }
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
}

function itemTypeStyle(type: string | null): ItemTypeStyle {
  if (!type) return DEFAULT_ITEM_TYPE_STYLE;
  return (
    ITEM_TYPE_STYLES[type] ?? {
      ...DEFAULT_ITEM_TYPE_STYLE,
      label: labelize(type.replace(/^Itinerary/, "")),
    }
  );
}

function itemTitle(item: RecordLike): string {
  const detail = asRecord(item.detail);
  return (
    firstText(
      nameFromRecord(asRecord(detail.hotel)),
      nameFromRecord(asRecord(detail.experience)),
      nameFromRecord(asRecord(detail.activity)),
      nameFromRecord(asRecord(detail.transportation_type)),
      nameFromRecord(asRecord(detail.itinerary_plan_item)),
      detail.name,
      item.name,
    ) ??
    (stringValue(item.id) ? `Item ${stringValue(item.id)}` : "Untitled item")
  );
}

function itemDetailLines(
  item: RecordLike,
  detail: RecordLike,
  arrangement?: RecordLike,
): string[] {
  const arrangementDetail = asRecord(arrangement?.detail);
  const lines = [
    routeLine(
      firstText(
        detail.departure_location,
        nameFromRecord(asRecord(detail.departure_transportation_point)),
      ),
      firstText(
        detail.arrival_location,
        nameFromRecord(asRecord(detail.arrival_transportation_point)),
      ),
    ),
    labeledNote("Location", detail.location),
    labeledNote("Room", nameFromRecord(asRecord(detail.hotel_room))),
    labeledNote("Meal", detail.meal_type),
    labeledNote("Vehicle", detail.vehicle_number),
    labeledNote("Seat", detail.seat_number),
    labeledNote("Supplier", arrangementDetail.supplier),
    labeledNote("Cancel", arrangement?.cancel_policy),
    labeledNote("Customer note", item.to_customer_note),
    labeledNote("Internal note", item.to_internal_note),
  ];
  return lines.filter((value): value is string => value != null);
}

function projectDisplayName(project: RecordLike): string {
  const agency = nameFromRecord(asRecord(project.agency));
  const customers = asRecords(project.customer_projects)
    .map((customerProject) =>
      nameFromRecord(asRecord(customerProject.customer)),
    )
    .filter((value): value is string => value != null);
  const travelers = stringValue(project.travelers_count);
  const composedName = [
    agency,
    customers.slice(0, 2).join(", "),
    travelers ? `${travelers}PAX` : null,
  ]
    .filter((value): value is string => value != null && value !== "")
    .join(" ");
  return (
    firstText(project.name, project.title, composedName) ?? "Untitled project"
  );
}

function routeLine(from: string | null, to: string | null): string | null {
  if (!from && !to) return null;
  if (from && to) return `${from} -> ${to}`;
  return from ?? to;
}

function labeledNote(label: string, value: unknown): string | null {
  const text = stringValue(value);
  return text ? `${label}: ${text}` : null;
}

function moneyPart(label: string, value: unknown): string | null {
  const number = numberValue(value);
  if (number == null || number === 0) return null;
  return `${label}: ${formatNumber(number)}`;
}

function quantityPart(value: unknown): string | null {
  const number = numberValue(value);
  if (number == null || number === 0) return null;
  return `Qty: ${formatNumber(number)}`;
}

function compactPriceParts(
  parts: string[],
): Array<{ label: string; value: string }> {
  const labels: Record<string, string> = {
    Actual: "A",
    Qty: "Qty",
    Quote: "Q",
    Sell: "S",
  };
  return parts.slice(0, 4).map((part) => {
    const [label, ...rest] = part.split(":");
    const value = rest.join(":").trim();
    return {
      label: labels[label] ?? label,
      value: value || part,
    };
  });
}

function formatTimeRange(start: string | null, end: string | null): string {
  if (start && end) return `${trimSeconds(start)}-${trimSeconds(end)}`;
  if (start) return trimSeconds(start);
  if (end) return `Until ${trimSeconds(end)}`;
  return "All day";
}

function timeToMinutes(value: string | null): number | null {
  if (!value) return null;
  const match = value.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function formatDateRange(start: string | null, end: string | null): string {
  if (start && end) return `${start} - ${end}`;
  return start ?? end ?? "N/A";
}

function trimSeconds(value: string): string {
  return value.replace(/:00$/, "");
}

function numberOrText(value: unknown): string | number | null {
  const number = numberValue(value);
  if (number != null) return number.toLocaleString();
  return stringValue(value);
}

function visitJapanTimesLabel(value: unknown): string | number | null {
  const text = stringValue(value);
  if (text === "first_time") return "First time";
  if (text === "second_time") return "Second time";
  if (text === "third_time_or_more") return "Third time or more";
  if (text === "blank") return "Unspecified";
  return numberOrText(value);
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function labelize(value: string | null): string {
  if (!value) return "";
  return value
    .replace(/^Itinerary/, "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function dayKey(itinerary: unknown, index: number): string {
  const day = asRecord(asRecord(itinerary).itinerary_day);
  return (
    stringValue(day.id) ??
    `${stringValue(day.day_number) ?? "day"}:${stringValue(day.date) ?? index}`
  );
}

function projectDataDayElementId(dayIndex: number): string {
  return `project-data-overview-day-${dayIndex}`;
}

function projectDataItemElementId(dayIndex: number, itemIndex: number): string {
  return `project-data-overview-day-${dayIndex}-item-${itemIndex}`;
}

function projectDataDayReferenceKey(dayIndex: number): string {
  return `day:${dayIndex}`;
}

function projectDataItemReferenceKey(item: ScheduleItem): string {
  return item.itemId
    ? `item-id:${item.itemId}`
    : `item:${item.dayIndex}:${item.itemIndex}`;
}

function findProjectDataOverviewElement(
  target: ProjectDataReferenceTarget,
  root: ParentNode,
): HTMLElement | null {
  if (target.kind === "itineraryItem") {
    if (target.itemId) {
      const byId = findProjectDataOverviewElementByData(
        root,
        "projectDataItemId",
        target.itemId,
      );
      if (byId) return byId;
    }

    if (target.dayIndex != null && target.itemIndex != null) {
      const byIndex = root.querySelector<HTMLElement>(
        `#${projectDataItemElementId(target.dayIndex, target.itemIndex)}`,
      );
      if (byIndex) return byIndex;
    }

    if (target.itemType) {
      const byType = findProjectDataOverviewItemByType(target, root);
      if (byType) return byType;
    }
  }

  if (target.dayIndex != null) {
    const byDayIndex = root.querySelector<HTMLElement>(
      `#${projectDataDayElementId(target.dayIndex)}`,
    );
    if (byDayIndex) return byDayIndex;
  }

  if (target.kind === "itineraryDay" && target.dayId) {
    const byDayId = findProjectDataOverviewElementByData(
      root,
      "projectDataDayId",
      target.dayId,
    );
    if (byDayId) return byDayId;
  }

  if (target.dayNumber != null) {
    return findProjectDataOverviewElementByData(
      root,
      "projectDataDayNumber",
      String(target.dayNumber),
    );
  }

  return null;
}

function findProjectDataOverviewItemByType(
  target: Extract<ProjectDataReferenceTarget, { kind: "itineraryItem" }>,
  root: ParentNode,
): HTMLElement | null {
  for (const element of root.querySelectorAll<HTMLElement>(
    "[data-project-data-item-type]",
  )) {
    if (element.dataset.projectDataItemType !== target.itemType) continue;
    if (
      target.dayIndex != null &&
      element.dataset.projectDataDayIndex !== String(target.dayIndex)
    ) {
      continue;
    }
    if (
      target.dayNumber != null &&
      findParentDayNumber(element) !== String(target.dayNumber)
    ) {
      continue;
    }
    return element;
  }
  return null;
}

function findParentDayNumber(element: HTMLElement): string | undefined {
  const dayElement = element.closest<HTMLElement>(
    "[data-project-data-day-number]",
  );
  return dayElement?.dataset.projectDataDayNumber;
}

function findProjectDataOverviewElementByData(
  root: ParentNode,
  datasetKey: string,
  value: string,
): HTMLElement | null {
  for (const element of root.querySelectorAll<HTMLElement>(
    "[data-project-data-ref-key]",
  )) {
    if (element.dataset[datasetKey] === value) return element;
  }
  return null;
}

function nameFromRecord(record: RecordLike): string | null {
  return firstText(
    record.name,
    record.name_en,
    record.name_ja,
    record.display_name,
    record.title,
    record.code,
  );
}

function firstText(...values: unknown[]): string | null {
  for (const value of values) {
    const text = stringValue(value);
    if (text) return text;
  }
  return null;
}

function stringValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === "blank") return null;
    return trimmed;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function numberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const normalized = value.replace(/,/g, "").trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}

function boolValue(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return false;
}

function asRecord(value: unknown): RecordLike {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as RecordLike)
    : {};
}

function asRecords(value: unknown): RecordLike[] {
  return Array.isArray(value)
    ? value
        .map((item) => asRecord(item))
        .filter((item) => Object.keys(item).length > 0)
    : [];
}
